// Public-config endpoint client — unauthenticated, available
// before SAML login so the Login page can render the right logo
// and help link.

import useSWR from 'swr';
import { parseErrorResponse } from './problem';

// v186 — currently-active scheduled banner. null when nothing is
// active right now (most of the time). The frontend treats null and
// undefined identically; the typing here is just a documentation
// hint that the server might omit the field on older builds.
export interface ActiveBanner {
  id: string;
  title: string;
  body: string;
  severity: 'info' | 'warning' | 'critical';
  starts_at: string;       // RFC3339
  ends_at: string;         // RFC3339
  dismissible: boolean;
  created_at: string;      // RFC3339
}

export interface PublicConfig {
  help_url: string;        // empty = no help button
  logo_url: string;        // empty = use built-in Wordmark
  version: string;         // build-time version string
  build_time: string;      // RFC3339 build timestamp; empty for dev builds
  active_banner: ActiveBanner | null;  // v186 scheduled banner
  // v220 — air-gapped status. TRUE means the deployment is in
  // air-gapped-by-default mode (egress denied unless an admin opted
  // in); FALSE means legacy "everything allowed". Surfaced in the
  // AppShell topbar as a small status badge so operators can tell
  // at a glance which mode the deployment is in.
  air_gapped_mode: boolean;
  // v237 — demo-data banner. TRUE means an `orbital seed` apply has
  // injected demo records on this install; the AppShell renders a
  // thin "demo data — orbital seed --reset to clear" strip under the
  // topbar so eval / sales-demo customers know they're not looking at
  // production state. seed_set carries the chosen set name
  // (minimal | default | full) for the same banner.
  seed_active?: boolean;
  seed_set?: string;
  // v241 — first-run setup wizard (item 76). TRUE on a fresh install
  // before an admin has walked the wizard; the App router auto-
  // redirects authenticated traffic to /setup until the operator
  // marks setup complete. /login + /setup + /admin/system/setup are
  // exempt from the redirect so the wizard itself stays reachable.
  setup_required?: boolean;
  // SAML master switch. FALSE means the server booted without a SAML SP
  // (saml.enabled=false) — the Login page hides the SSO tab and defaults
  // to local break-glass. Undefined on older builds → treated as enabled.
  saml_enabled?: boolean;
}

const json = async (url: string) => {
  const r = await fetch(url);
  if (!r.ok) throw await parseErrorResponse(r);
  return r.json();
};

// Cached fairly aggressively — branding + help URL rarely change
// within a session. The logo URL itself is cache-busted via the
// etag query param the server emits, so a fresh upload propagates
// without requiring a config-refresh.
//
// v186 — refreshInterval is one minute so a scheduled banner that
// flips active right after a user has loaded the app shows up
// without requiring a page reload. One round-trip per minute is
// trivial for an unauthenticated cacheable endpoint.
export function usePublicConfig() {
  return useSWR<PublicConfig>('/api/public-config', json, {
    refreshInterval: 60_000,
    revalidateOnFocus: true,
  });
}
